import * as React from "react";
import moment from "moment";
import DateTimePicker from "react-datetime-picker";
import { useEffect } from "react";
import { IUpdateUserIntro, IUser } from "../../resources/user/types";
import styles from "./intro-special.module.css";
import Modal from "../../components/modal";

interface IProps {
  onUpdateUserIntro: (values: IUpdateUserIntro) => void;
  user: IUser;
}

export default function IntroSpecial({ onUpdateUserIntro, user }: IProps) {
  const ref = React.useRef<any>(null);
  const [showEditDateModal, setShowEditDateModal] = React.useState<boolean>(
    false
  );
  const [isEditDateBusy, setIsEditDateBusy] = React.useState<boolean>(false);
  const [showCancellationModal, setShowCancellationModal] = React.useState<
    boolean
  >(false);
  const [isCancellationBusy, setIsCancellationBusy] = React.useState<boolean>(
    false
  );
  const [issueInvoiceDate, setIssueInvoiceDate] = React.useState<
    Date | undefined
  >(undefined);

  useEffect(() => {
    setIssueInvoiceDate(
      moment(user.issueInvoiceAt)
        .tz(user.timezone)
        .toDate()
    );
  }, [setIssueInvoiceDate, user]);

  const editDate = () => {
    setShowEditDateModal(true);
  };

  const closeEditDateModal = () => {
    setIsEditDateBusy(false);
    setShowEditDateModal(false);
  };

  const confirmCancellation = () => {
    setShowCancellationModal(true);
  };

  function pad(number: number) {
    let r = String(number);
    if (r.length === 1) {
      r = "0" + r;
    }
    return r;
  }

  const saveIntroSpecial = () => {
    if (isEditDateBusy || issueInvoiceDate === undefined) {
      return;
    }

    const localDate = moment.tz(
      issueInvoiceDate.getFullYear() +
        "-" +
        pad(issueInvoiceDate.getMonth() + 1) +
        "-" +
        pad(issueInvoiceDate.getDate()) +
        " " +
        pad(issueInvoiceDate.getHours()) +
        ":" +
        pad(issueInvoiceDate.getMinutes()),
      user.timezone
    );

    setIsEditDateBusy(true);
    onUpdateUserIntro({ issueInvoiceAt: localDate.utc().format() });
    setShowEditDateModal(false);
    setIsEditDateBusy(false);
  };

  const cancelIntroSpecial = () => {
    if (isCancellationBusy) {
      return;
    }

    setIsCancellationBusy(true);
    onUpdateUserIntro({ issueInvoiceAt: null });
    setShowCancellationModal(false);
    setIsCancellationBusy(false);
  };

  const closeCancellationModal = () => {
    setIsCancellationBusy(false);
    setShowCancellationModal(false);
  };

  return (
    <>
      <div ref={ref} className={styles.head}>
        <div className={styles.fieldContainer}>
          <div className={styles.label}>Invoice Date:</div>
          <div className={styles.field}>
            {moment(user.issueInvoiceAt)
              .tz(user.timezone)
              .format("MMM D, YYYY")}
            {" at "}
            {moment(user.issueInvoiceAt)
              .tz(user.timezone)
              .format("h:mma z")}
          </div>
        </div>
        <div className={styles.buttons}>
          <button onClick={editDate}>Edit Invoice Date</button>
          <button onClick={confirmCancellation}>Cancel Invoice</button>
        </div>
      </div>
      <Modal
        show={showEditDateModal}
        isLoading={!showEditDateModal}
        onClose={closeEditDateModal}
      >
        <div className={styles.modalContent}>
          <h1>Edit Invoice Date</h1>
          <p>
            This invoice date and time is when {user.firstName} will be billed
            for their regular monthly membership. If, for example,{" "}
            {user.firstName}'s schedule has changed and you need to adjust the
            billing date for their monthly membership, you can do that here.
          </p>
          <div className={styles.pickerContainer}>
            <DateTimePicker
              className={styles.datePicker}
              onChange={setIssueInvoiceDate}
              value={issueInvoiceDate}
              disableClock
              minDate={new Date(Date.now() + 60 * 60 * 24 * 1000)}
            />
          </div>
          <p>
            <small>
              NOTE: The time shown above is set in the timezone that your
              computer is currently using. If this is different than your gym's
              timezone, please adjust the time accordingly.
            </small>
          </p>
          <div className={styles.actionButtons}>
            <button
              className={styles.confirmButton}
              onClick={saveIntroSpecial}
              disabled={isEditDateBusy}
            >
              Save Changes
            </button>
            <button
              className={styles.cancelButton}
              onClick={closeEditDateModal}
              disabled={isEditDateBusy}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        show={showCancellationModal}
        isLoading={!showCancellationModal}
        onClose={closeCancellationModal}
      >
        <div className={styles.modalContent}>
          <h1>Invoice Cancelation</h1>
          <p>
            By clicking the <em>Confirm Cancelation</em> button below, this
            account will not be invoiced for a monthly membership on{" "}
            {moment(user.issueInvoiceAt)
              .tz(user.timezone)
              .format("MMM D, YYYY")}{" "}
            at{" "}
            {moment(user.issueInvoiceAt)
              .tz(user.timezone)
              .format("h:mma z")}
            . Any further membership or billing changes will need to be done in
            Pike13.
          </p>
          <div className={styles.actionButtons}>
            <button
              className={styles.confirmButton}
              onClick={cancelIntroSpecial}
              disabled={isCancellationBusy}
            >
              Confirm Cancelation
            </button>
            <button
              className={styles.cancelButton}
              onClick={closeCancellationModal}
              disabled={isCancellationBusy}
            >
              Do Nothing
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
